import Flickity from 'flickity';
import 'flickity-fade';

// The colourway swap (product/title-swatches.ts) replaces the gallery DOM
// and dispatches 'product:swap' — keep the instance module-scoped so it can
// be destroyed and rebuilt on the fresh nodes.
let productGallery: Flickity | null = null;

// Use the app bundle's existing instance so its background-image plugin
// observes the same loader; the product entry has a separate webpack runtime.
const unveilLazyElement = (element: HTMLElement | null) => {
  if (!element?.classList.contains('lazyload')) return;
  const lazySizes = (window as Window & {
    lazySizes?: { loader: { unveil(element: HTMLElement): void } };
  }).lazySizes;
  lazySizes?.loader.unveil(element);
};

// The transplanted slides use lazysizes data-src/data-srcset (not Flickity's
// data-flickity-lazyload). Explicitly request the selected image and neighbours
// even when the slider's transformed cells are outside the viewport observer.
const unveilGalleryImages = (gallery: Flickity, index: number) => {
  const count = gallery.cells.length;
  if (!count) return;
  [-1, 0, 1].forEach((offset) => {
    const image = gallery.cells[(index + offset + count) % count].element.querySelector('img');
    unveilLazyElement(image);
  });
};

const initGallery = async () => {
  const IMAGE_PLACEHOLDER_URL = document.querySelector<HTMLElement>('.sticky-buy__image')?.dataset.placeholder || '';

  // Only init HLS.js if there are videos
  const videos = [...document.querySelectorAll('.single-product__gallery-video')] as HTMLVideoElement[];
  if (videos.length) {
    // TODO: Does this dynamic import even work in adonis/prod mode?
    // Or is it no different than importing it at the top of the file?
    const Hls = (await import('hls.js')).default;

    // Init HLS.js
    if (Hls.isSupported()) {
      videos.forEach((video) => {
        const hlsUrl = video.dataset.hlsUrl;
        if (!hlsUrl) {
          return;
        }
        const hls = new Hls();
        hls.on(Hls.Events.MEDIA_ATTACHED, function() {
          console.debug('video and hls.js are now bound together!');
        });
        hls.on(Hls.Events.MANIFEST_PARSED, function(event, data) {
          console.debug('manifest loaded, found ' + data.levels.length + ' quality level');
          console.debug({ event, data });
        });
        hls.loadSource(hlsUrl);
        // bind them together
        hls.attachMedia(video);
      });
    }

    // TODO: Will need to make these auto play/pause when visible or not.
  }

  /**
   * Play video in current slide (if found) and pause all others
   * @param productGallery Flickity gallery instance
   * @param slideIndex Index of current slide
   */
  const playVideoIfPresent = (productGallery: Flickity, slideIndex: number) => {
    productGallery.cells.forEach((cell, cellIndex) => {
      // Check if cell has video
      let cellVideo = cell.element.querySelector('video');
      if (cellVideo) {
        // If cell is the current slide, play video
        if (cellIndex === slideIndex) {
          cellVideo.play();
        } else {
          // Otherwise, pause it
          cellVideo.pause();
        }
      }
    });
  };

  /**
   * Emit a custom event for product image change (or load),
   * so the sticky buy bar can update itself
   * @param flickity Flickity instance
   * @param index Index of the current slide
   */
  const emitImageChangeEvent = (flickity: Flickity, index: number) => {
    const cellElement = flickity.cells[index]?.element;
    if (!cellElement) return;
    const imageEl = cellElement.querySelector('img');
    let image = imageEl?.getAttribute('src') || imageEl?.getAttribute('data-src');

    // If no image, check for a video
    if (!image) {
      const videoEl = cellElement.querySelector('video');
      const poster = videoEl?.getAttribute('poster');
      image = poster;
    }

    // If still no image, use placeholder
    if (!image) {
      image = IMAGE_PLACEHOLDER_URL;
    }

    // console.debug({ flickity, index, image });

    // Make a custom event to trigger the sticky buy image update
    // This is listened for in the sticky buy module
    const event = new CustomEvent('product:image-change', {
      detail: {
        index,
        image,
      },
    });
    document.dispatchEvent(event);
  };

  // A previous gallery (pre-swap) is bound to detached DOM — drop it
  if (productGallery) {
    productGallery.destroy();
    productGallery = null;
  }

  const productGalleryElem = document.querySelector('.js-product-gallery');

  if (productGalleryElem) {
    const imageLightbox = !!productGalleryElem.closest('main[data-product-image-lightbox]');
    const gallery = new Flickity(productGalleryElem, {
      cellSelector: '.js-slide',
      initialIndex: '.js-slide[data-initial]',
      wrapAround: true,
      lazyLoad: 2,
      imagesLoaded: true,
      cellAlign: 'left',
      // Native page pinch/scroll owns touch gestures on the accessible image
      // viewer. Buttons and thumbnails provide gallery navigation instead.
      draggable: imageLightbox ? false : '>1',
      prevNextButtons: imageLightbox,
      selectedAttraction: 0.012,
      autoPlay: false,
      fade: true,
      on: {
        ready: function() {
          unveilGalleryImages(this, this.selectedIndex);
          emitImageChangeEvent(this, this.selectedIndex);
        },
      },
    });
    productGallery = gallery;

    if (imageLightbox) {
      const french = document.documentElement.lang.toLowerCase().startsWith('fr');
      productGalleryElem.querySelector('.flickity-prev-next-button.previous')?.setAttribute('aria-label', french ? 'Image précédente' : 'Previous image');
      productGalleryElem.querySelector('.flickity-prev-next-button.next')?.setAttribute('aria-label', french ? 'Image suivante' : 'Next image');
    }

    const thumbs = [...document.querySelectorAll<HTMLElement>('.js-product-gallery-thumb')];
    thumbs.forEach((thumb) => {
      unveilLazyElement(thumb);
    });

    thumbs.map((thumb) =>
      thumb.addEventListener('click', (btn) => {
        const index = thumbs.indexOf(btn.currentTarget as HTMLButtonElement);
        if (index >= 0) gallery.select(index);
      })
    );

    gallery.on('change', (index: number) => {
      document
        .querySelector('.js-product-gallery-thumb.selected')
        ?.classList.remove('selected');
      thumbs[index]?.classList.add('selected');
      unveilGalleryImages(gallery, index);

      // Make any videos play/pause when the slide changes
      playVideoIfPresent(gallery, index);

      emitImageChangeEvent(gallery, index);
    });

    // Initial play of video if present on first slide
    playVideoIfPresent(gallery, gallery.selectedIndex);
  }
};

document.addEventListener('DOMContentLoaded', initGallery);
document.addEventListener('product:swap', initGallery);
